import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import {
  appointmentBandOf,
  getClub,
  getClubOrgChart,
  getClubOverview,
  holdsASeat,
  isCoachingRole,
  listClubAppointments,
  listClubPitches,
  listClubTeams,
  seatHoldersOf,
  type AppointmentList,
  type AppointmentRead,
  type AppointmentRole,
  type ClubOverview,
  type ClubPitchList,
  type ClubRead,
  type Me,
  type OrgChart,
  type TeamList,
  type TeamRead,
} from '@volee/api-client'

import type { SquadClub } from './labels.ts'
import { meQueryOptions } from '../auth/me.ts'
import { api } from '../services/api.ts'

// Which società a page is about comes from `['me']` and never from a store:
// `club_short_name` is denormalised on the facet so no page needs a second request.

export interface OfficialClub {
  readonly id: number
  readonly shortName: string
}

/** The clubs of the incarichi that satisfy `keep`, each named once, in the order they come. */
function clubsWhere(
  me: Me | null,
  keep: (held: Me['appointments'][number]) => boolean,
): readonly OfficialClub[] {
  if (me === null) return []
  const byId = new Map<number, OfficialClub>()
  for (const held of me.appointments) {
    if (keep(held)) byId.set(held.club_id, { id: held.club_id, shortName: held.club_short_name })
  }
  return [...byId.values()]
}

/** The clubs where the caller holds an office incarico, each named once. */
export function officialClubsOf(me: Me | null): readonly OfficialClub[] {
  return clubsWhere(me, (held) => !isCoachingRole(held.role))
}

/** The clubs where the caller holds a seat on a squad, each named once. A coach still waiting for a
 *  squad, and staff of one without a seat, are not in the list: there is nothing of that società they hold. */
export function coachedClubsOf(me: Me | null): readonly OfficialClub[] {
  return clubsWhere(
    me,
    (held) => appointmentBandOf(held.role, held.team_id) === 'team' && holdsASeat(held),
  )
}

/** An incarico of a bench that holds no seat: a name on a squad, or on the società with no squad yet. */
export interface SeatlessIncarico {
  readonly club: OfficialClub
  /** `null` for staff not assigned to a squad. */
  readonly teamId: null
}

/** Only unassigned club staff may lack management access. Squad assignments always have coverage. */
function saysItOpensNothing(held: Me['appointments'][number], me: Me): boolean {
  if (held.team_id != null || holdsASeat(held)) return false
  return !servedClubsOf(me).some((club) => club.id === held.club_id)
}

/** Once per club where this person is waiting for an assignment and has no other access. */
export function seatlessIncarichiOf(me: Me | null): readonly SeatlessIncarico[] {
  if (me === null) return []
  const lines = new Map<number, SeatlessIncarico>()
  for (const held of me.appointments) {
    if (!saysItOpensNothing(held, me)) continue
    lines.set(held.club_id, {
      club: { id: held.club_id, shortName: held.club_short_name },
      teamId: null,
    })
  }
  return [...lines.values()]
}

/** The club a page that names none opens: the first of the reader's, since the dashboard is
 *  where a società gets picked. Those pages and every link to them ask here, so a link can
 *  tell where it lands. */
export function landingClub(clubs: readonly OfficialClub[]): OfficialClub | undefined {
  return clubs[0]
}

/** The club a page of the reader's own opens its band on, and the height that club earns. */
export interface LandingBand {
  /** `undefined` while the reader's clubs are read, `null` for a reader with none. */
  readonly club: OfficialClub | null | undefined
  readonly size: 'full' | 'half'
}

/** The whole band is for a crest to fill it: a reader with no club gets the half, and until the
 *  clubs are read the band stays the whole one, with the title alone. */
export function landingBandOf({ clubs, isPending }: OfficialClubs): LandingBand {
  if (isPending) return { club: undefined, size: 'full' }
  const club = landingClub(clubs)
  return club === undefined ? { club: null, size: 'half' } : { club, size: 'full' }
}

/** The clubs where the caller holds a seat, coaching included: twin of `clubs_the_caller_serves`. Staff without
 *  a seat is in none, as the server has it: every page of the società answers them 403. `officialClubsOf` is
 *  the narrower one. */
export function servedClubsOf(me: Me | null): readonly OfficialClub[] {
  return clubsWhere(me, holdsASeat)
}

export function useServedClubs(): OfficialClubs {
  const { data, isPending } = useQuery(meQueryOptions)
  return { clubs: servedClubsOf(data ?? null), isPending }
}

export interface OfficialClubs {
  readonly clubs: readonly OfficialClub[]
  readonly isPending: boolean
}

/** «Is this row ours» — the question the standings and the entries both ask of a club id.
 *  Empty for a reader with no incarico, so no badge, and never a second branch. */
export function useOurClubIds(): ReadonlySet<number> {
  const { clubs } = useServedClubs()
  return new Set(clubs.map((club) => club.id))
}

export function useCoachedClubs(): OfficialClubs {
  const { data, isPending } = useQuery(meQueryOptions)
  return { clubs: coachedClubsOf(data ?? null), isPending }
}

export function useOfficialClubs(): OfficialClubs {
  const { data, isPending } = useQuery(meQueryOptions)
  return { clubs: officialClubsOf(data ?? null), isPending }
}

/** The club a page is about, named once it is read: what goes over the title of a page of one of
 *  its things. `undefined` while the name is on its way, or while the page has no id yet. */
export function useNamedClub(clubId: number | undefined): OfficialClub | undefined {
  const club = useClubDetail(clubId)
  if (clubId === undefined || club.data === undefined) return undefined
  return { id: clubId, shortName: club.data.short_name }
}

/** The key the dashboard and the scheda share: a save on one has to show on the other. */
export function clubDetailKey(clubId: number): readonly unknown[] {
  return ['club', clubId]
}

export function clubDetailQuery(clubId: number) {
  return queryOptions<ClubRead>({
    queryKey: clubDetailKey(clubId),
    queryFn: () => getClub(api, clubId),
  })
}

export function clubOverviewKey(clubId: number): readonly unknown[] {
  return ['club-overview', clubId]
}

/** The reader's bond with the club and its three counts: what the page of a club shows
 *  follows the bond, so a reader with none never asks a read that would answer 403. */
export function clubOverviewQuery(clubId: number) {
  return queryOptions<ClubOverview>({
    queryKey: clubOverviewKey(clubId),
    queryFn: () => getClubOverview(api, clubId),
  })
}

/** The club's detail, read once there is an id. `skipToken` stops the request, not the cache
 *  entry — the key is still built here. `0` is safe to park it under: ids are database-generated
 *  and start at 1. */
function useClubDetail(clubId: number | undefined) {
  const detail = clubDetailQuery(clubId ?? 0)
  return useQuery({ ...detail, queryFn: clubId === undefined ? skipToken : detail.queryFn })
}

/** Shared club images: failed reads leave the hero’s default ground and crest standing. */
export function useClubImages(clubId: number | undefined) {
  const club = useClubDetail(clubId)
  return {
    crestUrl: club.data?.logo_display_url ?? undefined,
    coverUrl: club.data?.cover_display_url ?? undefined,
  }
}

export function useClubCrest(clubId: number | undefined): string | undefined {
  return useClubImages(clubId).crestUrl
}

/** The club a squad is drawn with: the same name and crest wherever a page shows its squads. */
export function useSquadClub(club: OfficialClub): SquadClub {
  return { name: club.shortName, crestUrl: useClubCrest(club.id) }
}

/** The organigramma, and the squads: shared keys, so a new incarico shows on every page that
 *  lists one. Written by hand in four places before this, which is how the dashboard and the
 *  people list could disagree about the same club. */
export function clubAppointmentsKey(clubId: number): readonly unknown[] {
  return ['club-appointments', clubId]
}

export function clubAppointmentsQuery(clubId: number) {
  return queryOptions<AppointmentList>({
    queryKey: clubAppointmentsKey(clubId),
    queryFn: () => listClubAppointments(api, clubId),
  })
}

/** The organigramma without a contact in it, the one a page shows. Under the key above, so
 *  every write that refreshes the organigramma refreshes this as well. */
export function clubOrgChartQuery(clubId: number) {
  return queryOptions<OrgChart>({
    queryKey: [...clubAppointmentsKey(clubId), 'org-chart'],
    queryFn: () => getClubOrgChart(api, clubId),
  })
}

export function clubTeamsQuery(clubId: number) {
  return queryOptions<TeamList>({
    queryKey: ['club-teams', clubId],
    queryFn: () => listClubTeams(api, clubId),
  })
}

/** The pitches the club plays on, the primary first: the API's order, kept as it comes. */
export function clubPitchesQuery(clubId: number) {
  return queryOptions<ClubPitchList>({
    queryKey: ['club-pitches', clubId],
    queryFn: () => listClubPitches(api, clubId),
  })
}

/** The allenatori among a bench's rows, a dirigente di squadra left out: who a squad calls «Mister», and
 *  what its counter counts. `coachesOf` of `@volee/api-client` names the whole bench. */
export function coachRows<T extends { readonly role: AppointmentRole }>(
  rows: readonly T[],
): readonly T[] {
  return rows.filter((one) => one.role === 'coach')
}

/** The squads with nobody in their seats, not «those this person does not coach»
 *  (apps/frontend/docs/la-societa.md): an incarico without a seat opens nothing (#366). */
export function unseatedSquads(
  teams: readonly TeamRead[],
  organigramma: readonly AppointmentRead[],
): readonly TeamRead[] {
  return teams.filter((team) => seatHoldersOf(organigramma, team.id).length === 0)
}
